import React from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type AdTextAsset = {
    id: string;
    asset_type: 'HEADLINE' | 'DESCRIPTION';
    before_text: string | null;
    suggested_text: string;
    is_changed: boolean;
};

export type AdTextSuggestion = {
    ad_id: string;
    ad_group_name: string;
    reason?: string;
    before_ad_strength?: string;
    suggested_ad_strength?: string;
    headlines: AdTextAsset[];
    descriptions: AdTextAsset[];
};

export type AdTextFormData = {
    headlines: Record<string, string>;
    descriptions: Record<string, string>;
};

type Props = {
    ads: AdTextSuggestion[];
    formData: Record<string, AdTextFormData>;
    onChange: (id: string, data: AdTextFormData) => void;
    platform?: "GOOGLE" | "META";
};

// -----------------------------------------------------------------------------
// Component: AdTextForm
// -----------------------------------------------------------------------------
const AdTextForm: React.FC<Props> = ({ ads, formData, onChange, platform = "GOOGLE" }) => {
    // 既存の広告の有効性（Ad Strength）の表示用ヘルパー
    const getStrengthBadgeClass = (strength?: string) => {
        if (!strength) return styles.strengthUnknown;
        const s = strength.toUpperCase();
        if (s === "EXCELLENT" || s === "非常に高い" || s === "高い") return styles.strengthHigh;
        if (s === "GOOD" || s === "平均的") return styles.strengthMedium;
        return styles.strengthLow;
    };

    const formatStrengthLabel = (strength?: string) => {
        if (!strength) return "未評価";
        const s = strength.toUpperCase();
        if (s === "EXCELLENT") return "非常に高い";
        if (s === "GOOD") return "高い";
        if (s === "AVERAGE") return "平均的";
        if (s === "POOR") return "低い";
        return strength;
    };

    // Google広告の文字数カウント (全角=2, 半角=1)
    const getCharCount = (str: string): number => {
        if (!str) return 0;
        let count = 0;
        for (let i = 0; i < str.length; i++) {
            const code = str.charCodeAt(i);
            // 基本ラテン文字と半角カナは1、それ以外（全角文字）は2としてカウント
            if ((code >= 0x0001 && code <= 0x007E) || (code >= 0xFF61 && code <= 0xFF9F)) {
                count += 1;
            } else {
                count += 2;
            }
        }
        return count;
    };

    const headlineTitle = platform === "META" ? "タイトル / 見出し" : "見出し";
    const descriptionTitle = platform === "META" ? "本文 / 説明文" : "説明文";
    const headlinePlaceholder = platform === "META" ? "タイトルを入力" : "見出しを入力";
    const descriptionPlaceholder = platform === "META" ? "本文を入力" : "説明文を入力";
    const headlineLimitLabel = platform === "META" ? "推奨: 簡潔で読みやすい表現" : "全角15文字 / 半角30文字";
    const descriptionLimitLabel = platform === "META" ? "推奨: 要点が伝わる本文" : "全角45文字 / 半角90文字";

    return (
        <div className={styles.adTextFormContainer}>
            {ads.map((ad) => {
                const currentData = formData[ad.ad_id] || { headlines: {}, descriptions: {} };

                const handleHeadlineChange = (assetId: string, val: string) => {
                    const nextData = { ...currentData, headlines: { ...currentData.headlines, [assetId]: val } };
                    onChange(ad.ad_id, nextData);
                };

                const handleDescChange = (assetId: string, val: string) => {
                    const nextData = { ...currentData, descriptions: { ...currentData.descriptions, [assetId]: val } };
                    onChange(ad.ad_id, nextData);
                };

                return (
                    <section key={ad.ad_id} className={styles.campaignCard}>
                        <div className={styles.campaignName}>{ad.ad_group_name}</div>

                        {(ad.before_ad_strength || ad.suggested_ad_strength) && (
                            <div className={styles.strengthIndicator}>
                                <span className={styles.strengthLabel}>広告の有効性:</span>
                                <span className={`${styles.strengthBadge} ${getStrengthBadgeClass(ad.before_ad_strength)}`}>
                                    {formatStrengthLabel(ad.before_ad_strength)}
                                </span>
                                <span className={styles.strengthArrow}>→</span>
                                <span className={`${styles.strengthBadge} ${getStrengthBadgeClass(ad.suggested_ad_strength)}`}>
                                    {formatStrengthLabel(ad.suggested_ad_strength)} (AI予測)
                                </span>
                            </div>
                        )}

                        {ad.reason && (
                            <div className={styles.reasonBar}>
                                <strong>Aibex提案理由:</strong> {ad.reason}
                            </div>
                        )}

                        <div className={styles.rsaContainer}>
                            {/* Headlines Section */}
                            <div className={styles.assetSection}>
                                <h4 className={styles.assetSectionTitle}>{headlineTitle} ({headlineLimitLabel})</h4>
                                <div className={styles.assetList}>
                                    {ad.headlines.map((asset, index) => {
                                        const val = currentData.headlines[asset.id] !== undefined ? currentData.headlines[asset.id] : asset.suggested_text;
                                        const count = getCharCount(val);
                                        const maxLimit = 30;
                                        const isError = count > maxLimit;

                                        return (
                                            <div key={asset.id} className={`${styles.assetRow} ${asset.is_changed ? styles.isChanged : ''} ${isError ? styles.hasError : ''}`}>
                                                <div className={styles.assetIndex}>{index + 1}</div>
                                                <div className={styles.assetBefore}>
                                                    <span className={styles.assetSubLabel}>現在の設定:</span>
                                                    <p>{asset.before_text || <span className={styles.textMuted}>(新規追加)</span>}</p>
                                                </div>
                                                <div className={styles.assetArrow}>→</div>
                                                <div className={styles.assetAfter}>
                                                    <span className={styles.assetSubLabel}>AI提案 (編集可):</span>
                                                    <input
                                                        type="text"
                                                        value={val}
                                                        onChange={(e) => handleHeadlineChange(asset.id, e.target.value)}
                                                        className={`${styles.textInputFull} ${isError ? styles.inputError : ''}`}
                                                        placeholder={headlinePlaceholder}
                                                    />
                                                    <div className={`${styles.charCount} ${isError ? styles.textError : ''}`}>
                                                        {count} / {maxLimit}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Descriptions Section */}
                            <div className={styles.assetSection}>
                                <h4 className={styles.assetSectionTitle}>{descriptionTitle} ({descriptionLimitLabel})</h4>
                                <div className={styles.assetList}>
                                    {ad.descriptions.map((asset, index) => {
                                        const val = currentData.descriptions[asset.id] !== undefined ? currentData.descriptions[asset.id] : asset.suggested_text;
                                        const count = getCharCount(val);
                                        const maxLimit = 90;
                                        const isError = count > maxLimit;

                                        return (
                                            <div key={asset.id} className={`${styles.assetRow} ${styles.descRow} ${asset.is_changed ? styles.isChanged : ''} ${isError ? styles.hasError : ''}`}>
                                                <div className={styles.assetIndex}>{index + 1}</div>
                                                <div className={styles.assetBefore}>
                                                    <span className={styles.assetSubLabel}>現在の設定:</span>
                                                    <p className={styles.descText}>{asset.before_text || <span className={styles.textMuted}>(新規追加)</span>}</p>
                                                </div>
                                                <div className={styles.assetArrow}>→</div>
                                                <div className={styles.assetAfter}>
                                                    <span className={styles.assetSubLabel}>AI提案 (編集可):</span>
                                                    <textarea
                                                        value={val}
                                                        onChange={(e) => handleDescChange(asset.id, e.target.value)}
                                                        className={`${styles.textAreaFull} ${isError ? styles.inputError : ''}`}
                                                        rows={3}
                                                        placeholder={descriptionPlaceholder}
                                                    />
                                                    <div className={`${styles.charCount} ${isError ? styles.textError : ''}`}>
                                                        {count} / {maxLimit}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default AdTextForm;
